import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { isGuest as checkIsGuest, getRolesDisplay } from '../utils/roleUtils';
import ConfirmationModal from '../components/ConfirmationModal';
import { ModalShell, ModalActions } from '../components/modals/core';
import AttributeLabel from '../components/AttributeLabel';
import PushNotificationSettings from '../components/PushNotificationSettings';
import { getAttributeIcon } from '../utils/attributeIcons';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import {
  UserCircleIcon,
  CameraIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  HomeIcon,
  CalendarDaysIcon,
  TicketIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  NewspaperIcon,
  LockClosedIcon,
  TrashIcon,
  FingerPrintIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

type IconComp = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

/** Kennzahl-Kachel (v3): Caps-Label mit Icon + fetter Wert. */
const StatTile: React.FC<{
  icon: IconComp;
  label: string;
  value: React.ReactNode;
  valueClass?: string;
  mono?: boolean;
  sub?: React.ReactNode;
}> = ({ icon: Icon, label, value, valueClass = 'text-gray-900 dark:text-white', mono, sub }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-4">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
      {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
      <span className="truncate">{label}</span>
    </div>
    <div className={`text-base font-bold ${mono ? 'font-mono tabular-nums ' : ''}${valueClass}`}>{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{sub}</div>}
  </div>
);

/** Label-links / Wert-rechts Zeile mit Icon-Label (v3 + Icon-Ergänzung). */
const InfoRow: React.FC<{
  icon?: IconComp;
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  valueClass?: string;
}> = ({ icon: Icon, label, children, mono, valueClass }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700/60">
    <span className="flex items-center gap-2 text-base text-gray-500 dark:text-gray-400 min-w-0">
      {Icon && <Icon className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />}
      <span className="truncate">{label}</span>
    </span>
    <span className={`text-base font-semibold text-right ${mono ? 'font-mono tabular-nums ' : ''}${valueClass || 'text-gray-900 dark:text-white'}`}>
      {children}
    </span>
  </div>
);

/** Grüne Sektions-Überschrift mit Icon (Größe wie MyDogs: text-lg). */
const SectionTitle: React.FC<{ icon: IconComp; children: React.ReactNode }> = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3">
    <Icon className="h-5 w-5 flex-shrink-0 opacity-80" aria-hidden="true" />
    {children}
  </div>
);

/** Kleines Ja/Nein-Badge für Freigaben (v3). */
const YesNoBadge: React.FC<{ yes: boolean }> = ({ yes }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
      yes
        ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    }`}
  >
    {yes ? 'Ja' : 'Nein'}
  </span>
);

const Profile: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const isGuest = checkIsGuest(user);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [displayData, setDisplayData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passkeys, setPasskeys] = useState<Array<{
    id: number;
    device_name: string | null;
    transports: string | null;
    backed_up: number;
    created_at: string;
    last_used_at: string | null;
  }>>([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = browserSupportsWebAuthn();

  // Passkeys laden
  const loadPasskeys = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/webauthn/credentials');
      if (res.data.success) setPasskeys(res.data.passkeys || []);
    } catch (err) {
      console.warn('[Profile] Passkeys konnten nicht geladen werden', err);
    }
  }, []);

  useEffect(() => {
    if (passkeySupported) loadPasskeys();
  }, [passkeySupported, loadPasskeys]);

  const handleAddPasskey = async () => {
    setPasskeyBusy(true);
    try {
      const beginRes = await apiClient.post('/api/webauthn/register/begin', {});
      const credential = await startRegistration({ optionsJSON: beginRes.data });
      const deviceName = (typeof navigator !== 'undefined' && (navigator as any).userAgentData?.platform)
        || (navigator?.platform || 'Unbekanntes Gerät');
      const completeRes = await apiClient.post('/api/webauthn/register/complete', {
        credential,
        deviceName: deviceName.toString().slice(0, 60)
      });
      if (completeRes.data.success) {
        await loadPasskeys();
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Passkey gespeichert',
          message: 'Du kannst dich nun mit Passkey anmelden.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, visible: false }))
        });
      }
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // User-Abbruch — keine Fehlermeldung
      } else {
        console.error('[Profile] Passkey hinzufügen fehlgeschlagen:', err);
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: err?.response?.data?.error || err?.message || 'Passkey konnte nicht hinzugefügt werden.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, visible: false }))
        });
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleDeletePasskey = async (id: number) => {
    setPasskeyBusy(true);
    try {
      await apiClient.delete(`/api/webauthn/credentials/${id}`);
      await loadPasskeys();
    } catch (err: any) {
      console.error('[Profile] Passkey löschen fehlgeschlagen:', err);
    } finally {
      setPasskeyBusy(false);
    }
  };
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (formData.mg_nr && formData.foto_path) {
      const token = localStorage.getItem('jwt_token');
      const timestamp = formData.foto_path_updated || Date.now();
      const url = `${BASE_URL}/api/members/user-image-url/${formData.mg_nr}?size=L&token=${token}&ts=${timestamp}`;
      setProfileImageUrl(url);
      setImageError(false);
    } else {
      setProfileImageUrl(null);
    }
  }, [formData.mg_nr, formData.foto_path, formData.foto_path_updated, BASE_URL]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/members/profile');
      const data = res.data;
      const newFormData = {
        vorname: data.vorname || '',
        name: data.name || '',
        strasse: data.strasse || '',
        plz: data.plz || '',
        ort: data.ort || '',
        tel_nummer: data.tel_nummer || '',
        email: data.email || '',
        geb_am: data.geb_am || '',
        eintritt: data.eintritt || '',
        mitglied_nr: data.mitglied_nr || '',
        mg_nr: data.mg_nr || '',
        vereinsposition: 'Mitglied',
        mitgliedBezahlt: data.beitrag_befreit ? 'beitragsfrei' : data.beitrag_bezahlt ? 'bezahlt' : 'offen',
        beitrag_befreit: !!data.beitrag_befreit,
        beitrag_bezahlt: !!data.beitrag_bezahlt,
        beitrag_datum: data.beitrag_datum || null,
        beitrag_betrag: data.beitrag_betrag ?? null,
        datenschutzerklaerung: data.datenschutzerklaerung || 0,
        homepage: data.homepage || 0,
        facebook: data.facebook || 0,
        presse: data.presse || 0,
        vorstand: data.vorstand || 0,
        foto_path: data.foto_path || '',
        foto_path_updated: data.foto_path_updated || null,
        gender: data.gender !== undefined && data.gender !== null ? data.gender : null,
        // Verzehrliste: Standard AN (1), wenn nicht gesetzt
        verzehrliste: data.verzehrliste === 0 ? 0 : 1,
      };
      setFormData(newFormData);
      setDisplayData({
        ...newFormData,
        geb_am: formatGermanDate(data.geb_am),
        eintritt: formatGermanDate(data.eintritt),
        datenschutzerklaerung: data.datenschutzerklaerung === 1 ? 'Ja' : 'Nein',
        homepage: data.homepage === 1 ? 'Ja' : 'Nein',
        facebook: data.facebook === 1 ? 'Ja' : 'Nein',
        presse: data.presse === 1 ? 'Ja' : 'Nein',
        vorstand: data.vorstand === 1 ? 'Ja' : 'Nein',
        verzehrliste: newFormData.verzehrliste === 1 ? 'Ja' : 'Nein',
        gender: newFormData.gender,
      });
      
      let rolesString = 'Mitglied';
      if (user?.roles && Array.isArray(user.roles)) {
        rolesString = getRolesDisplay(user);
      } else if (data.roles && Array.isArray(data.roles)) {
        rolesString = getRolesDisplay(data);
      } else if (data.mg_nr) {
        try {
          const rolesRes = await apiClient.get(`/api/roles/user_roles?mg_nr=${data.mg_nr}`);
          const rolesData = rolesRes.data;
          if (rolesData.success) {
            rolesString = rolesData.roles.join(', ') || 'Mitglied';
          }
        } catch (error: any) {
          if (error.response?.status !== 401) {
            console.error('Fehler beim Abrufen der Rollen:', error);
          }
          rolesString = getRolesDisplay(data);
        }
      }
      
      setFormData((prev: any) => ({ ...prev, vereinsposition: rolesString }));
      setDisplayData((prev: any) => ({ ...prev, vereinsposition: rolesString }));
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Fehler beim Abrufen der Profildaten:', err);
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: 'Fehler beim Laden der Profildaten.',
          variant: 'single',
          onConfirm: () => {},
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    if (name === 'geb_am' || name === 'eintritt') {
      setDisplayData((prev: any) => ({ ...prev, [name]: formatGermanDate(value) }));
    } else if (['datenschutzerklaerung', 'homepage', 'facebook', 'presse'].includes(name)) {
      setDisplayData((prev: any) => ({ ...prev, [name]: value === '1' ? 'Ja' : 'Nein' }));
    } else {
      setDisplayData((prev: any) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleVerzehrlisteToggle = useCallback(() => {
    setFormData((prev: any) => {
      const next = prev.verzehrliste === 1 ? 0 : 1;
      setDisplayData((d: any) => ({ ...d, verzehrliste: next === 1 ? 'Ja' : 'Nein' }));
      return { ...prev, verzehrliste: next };
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Prepare data with date-only format
      const submitData = {
        ...formData,
        geb_am: formData.geb_am ? new Date(formData.geb_am).toISOString().split('T')[0] : '',
        eintritt: formData.eintritt ? new Date(formData.eintritt).toISOString().split('T')[0] : ''
      };
      const res = await apiClient.put(`/api/members/user/${formData.mg_nr}`, submitData);
      const data = res.data;
      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Profil erfolgreich aktualisiert!',
        variant: 'single',
        onConfirm: () => {},
      });
      setEditMode(false);
      setDisplayData((prev: any) => ({
        ...prev,
        geb_am: formatGermanDate(formData.geb_am),
        eintritt: formatGermanDate(formData.eintritt),
        datenschutzerklaerung: formData.datenschutzerklaerung === 1 ? 'Ja' : 'Nein',
        homepage: formData.homepage === 1 ? 'Ja' : 'Nein',
        facebook: formData.facebook === 1 ? 'Ja' : 'Nein',
        presse: data.presse === 1 ? 'Ja' : 'Nein',
        gender: formData.gender,
      }));
    } catch (err: any) {
      if (err.response?.status !== 401) {
        const errorMessage = err.response?.data?.message || '😓 Fehler beim Speichern.';
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: errorMessage,
          variant: 'single',
          onConfirm: () => {},
        });
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
        title: 'Netzwerkfehler',
        message: '😓 Netzwerkfehler. Bitte später versuchen.',
        variant: 'single',
        onConfirm: () => {}, });
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImageLoading(true);
    const uploadData = new FormData();
    uploadData.append('photo', file);

    try {
      const res = await apiClient.post('/api/auth/upload_photo', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const data = res.data;
      const newTimestamp = Date.now();
      setFormData((prev: any) => ({ 
        ...prev, 
        foto_path: data.file_path,
        foto_path_updated: newTimestamp 
      }));
      setDisplayData((prev: any) => ({ 
        ...prev, 
        foto_path: data.file_path,
        foto_path_updated: newTimestamp 
      }));
      
      const token = localStorage.getItem('jwt_token');
      const newUrl = `${BASE_URL}/api/members/user-image-url/${formData.mg_nr}?size=L&token=${token}&ts=${newTimestamp}&nocache=${Date.now()}`;
      setProfileImageUrl(newUrl);
      
      setConfirmModal({
          visible: true,
          type: 'success',
        title: 'Erfolg',
        message: '🎉 Foto erfolgreich hochgeladen!',
        variant: 'single',
        onConfirm: () => {}, });
    } catch (err: any) {
      if (err.response?.status !== 401) {
        const errorMessage = err.response?.data?.message || '😓 Fehler beim Hochladen.';
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: errorMessage,
          variant: 'single',
          onConfirm: () => {},
        });
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
        title: 'Netzwerkfehler',
        message: '😓 Netzwerkfehler beim Hochladen.',
        variant: 'single',
        onConfirm: () => {}, });
        console.error(err);
      }
    } finally {
      setImageLoading(false);
      const fileInput = document.getElementById('image-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Profilbild löschen',
      message: 'Möchten Sie Ihr Profilbild wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        setImageLoading(true);
        try {
          const res = await apiClient.delete('/api/members/delete-image');
          if (res.data.success) {
            setFormData((prev: any) => ({
              ...prev,
              foto_path: null,
              foto_path_updated: null
            }));
            setDisplayData((prev: any) => ({
              ...prev,
              foto_path: null,
              foto_path_updated: null
            }));
            setProfileImageUrl(null);
            setImageError(false);

            setConfirmModal({
              visible: true,
              type: 'success',
              title: 'Erfolg',
              message: 'Profilbild wurde gelöscht.',
              variant: 'single',
              onConfirm: () => {},
            });
          }
        } catch (err: any) {
          if (err.response?.status !== 401) {
            const errorMessage = err.response?.data?.message || 'Fehler beim Löschen des Profilbilds.';
            setConfirmModal({
              visible: true,
              type: 'error',
              title: 'Fehler',
              message: errorMessage,
              variant: 'single',
              onConfirm: () => {},
            });
          }
        } finally {
          setImageLoading(false);
        }
      },
    });
  };

  // Passwort-Regeln (identisch zum Backend)
  const passwordRules = [
    { label: 'Mindestens 8 Zeichen', test: (pw: string) => pw.length >= 8 },
    { label: 'Mindestens ein Großbuchstabe', test: (pw: string) => /[A-Z]/.test(pw) },
    { label: 'Mindestens eine Zahl', test: (pw: string) => /[0-9]/.test(pw) },
    { label: 'Mindestens ein Sonderzeichen', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
  ];
  const isPasswordValid = passwordRules.every((rule) => rule.test(passwordData.newPassword));
  const passwordsMatch =
    passwordData.confirmPassword.length > 0 &&
    passwordData.newPassword === passwordData.confirmPassword;
  const canSubmitPassword =
    passwordData.currentPassword.length > 0 && isPasswordValid && passwordsMatch;

  const handlePasswordChange = async () => {
    if (!isPasswordValid) {
      setConfirmModal({
          visible: true,
          type: 'error',
        title: 'Fehler',
        message: 'Das neue Passwort erfüllt nicht alle Anforderungen (min. 8 Zeichen, Großbuchstabe, Zahl, Sonderzeichen).',
        variant: 'single',
        onConfirm: () => {}, });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setConfirmModal({
          visible: true,
          type: 'error',
        title: 'Fehler',
        message: 'Die neuen Passwörter stimmen nicht überein',
        variant: 'single',
        onConfirm: () => {}, });
      return;
    }

    try {
      const res = await apiClient.post('/api/members/change_password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (res.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
        title: 'Erfolg',
        message: 'Passwort erfolgreich geändert!',
        variant: 'single',
        onConfirm: () => {}, });
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error: any) {
      setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: error.response?.data?.message || 'Fehler beim Ändern des Passworts',
          variant: 'single',
          onConfirm: () => {},
        });
    }
  };

  const formatGermanDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Profilvollständigkeit rollenabhängig prüfen
  const requiredFields = isGuest
    ? ['vorname', 'name', 'email', 'strasse']
    : ['vorname', 'name', 'email', 'strasse', 'eintritt'];
  const isComplete = requiredFields.every((field) => formData[field] && formData[field] !== '') && formData.foto_path;
  const currentYear = new Date().getFullYear();

  if (loading && !formData.vorname) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-2/5 h-72 sm:h-80 bg-gray-200 dark:bg-gray-700 animate-pulse" />
            <div className="lg:w-3/5 p-6 space-y-4">
              <div className="h-7 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const memberSinceYear = formData.eintritt
    ? String(new Date(formData.eintritt).getFullYear())
    : '–';
  const genderLabel =
    displayData.gender === false || displayData.gender === 0
      ? 'Männlich'
      : displayData.gender === true || displayData.gender === 1
        ? 'Weiblich'
        : 'Nicht angegeben';
  const passkeySubtitle =
    passkeys.length > 0
      ? `${passkeys.length} Passkey${passkeys.length > 1 ? 's' : ''} aktiv${
          passkeys[0]?.device_name ? ` · ${passkeys[0].device_name}` : ''
        }`
      : 'Kein Passkey aktiv';

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-8">
      {/* Seiten-Header (einheitlich wie andere Seiten) */}
      <header className="app-page-header hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:block">
        <div className="flex items-center justify-between gap-3 p-5 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <UserCircleIcon className="h-7 w-7 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold text-gray-900 dark:text-white md:text-[28px]">Mein Profil</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Verwalte deine persönlichen Daten</p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2 md:gap-3">
            {editMode ? (
              <>
                <button
                  onClick={() => setEditMode(false)}
                  disabled={loading}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
                  title="Abbrechen"
                >
                  <XMarkIcon className="h-5 w-5" />
                  <span>Abbrechen</span>
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl bg-success-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-success-700 active:bg-success-800 disabled:opacity-60"
                  title="Speichern"
                >
                  <CheckIcon className="h-5 w-5" />
                  <span>Speichern</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditMode(true)}
                disabled={loading}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-success-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-success-700 active:bg-success-800 disabled:opacity-60"
                title="Bearbeiten"
              >
                <PencilIcon className="h-5 w-5" />
                <span>Bearbeiten</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile: Aktion unter der App-Leiste */}
      <div className="flex items-center gap-2 sm:hidden">
        {editMode ? (
          <>
            <button
              onClick={() => setEditMode(false)}
              disabled={loading}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
              title="Abbrechen"
            >
              <XMarkIcon className="h-5 w-5" />
              <span>Abbrechen</span>
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-success-600 px-4 text-base font-semibold text-white shadow-sm transition-colors hover:bg-success-700 active:bg-success-800 disabled:opacity-60"
              title="Speichern"
            >
              <CheckIcon className="h-5 w-5" />
              <span>Speichern</span>
            </button>
          </>
        ) : (
          <button
            onClick={() => setEditMode(true)}
            disabled={loading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-success-600 px-4 text-base font-semibold text-white shadow-sm transition-colors hover:bg-success-700 active:bg-success-800 disabled:opacity-60"
            title="Bearbeiten"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Profil bearbeiten</span>
          </button>
        )}
      </div>

      {/* Identity-Header (Design „Profilseite v3") */}
      <div className="flex items-center gap-4 sm:gap-5 flex-wrap">
        {/* Foto */}
        <div className="relative flex-none">
          {imageLoading ? (
            <div className="h-20 w-20 sm:h-[84px] sm:w-[84px] rounded-2xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700">
              <div className="animate-spin rounded-full h-7 w-7 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : profileImageUrl && !imageError ? (
            <img
              src={profileImageUrl}
              alt="Profilbild"
              className="h-20 w-20 sm:h-[84px] sm:w-[84px] rounded-2xl object-cover shadow-sm ring-1 ring-gray-200 dark:ring-gray-700"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="h-20 w-20 sm:h-[84px] sm:w-[84px] rounded-2xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700">
              <UserIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
            </div>
          )}
          {editMode && !imageLoading && (
            <div className="absolute -bottom-2 -right-2 flex gap-1.5">
              <label className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 p-1.5 rounded-lg shadow-md border border-gray-200 dark:border-gray-600 transition-colors cursor-pointer" title="Profilbild ändern">
                <CameraIcon className="h-4 w-4" />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handlePhotoUpload}
                  disabled={loading || imageLoading}
                />
              </label>
              {profileImageUrl && !imageError && (
                <button
                  type="button"
                  onClick={handlePhotoDelete}
                  disabled={loading || imageLoading}
                  className="bg-error-500 hover:bg-error-600 text-white p-1.5 rounded-lg shadow-md transition-colors"
                  title="Profilbild löschen"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Name + Badges */}
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Mitgliederprofil{formData.mitglied_nr ? ` · Nr. ${formData.mitglied_nr}` : ''}
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white leading-none tracking-tight truncate">
            {`${formData.vorname} ${formData.name}`.trim() || 'Mitglied'}
          </h1>
          <div className="flex gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300">
              <ShieldCheckIcon className="h-3.5 w-3.5" />
              {formData.vereinsposition || 'Mitglied'}
            </span>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                isComplete
                  ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                  : 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
              }`}
            >
              {isComplete ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
              {isComplete ? 'Profil vollständig' : 'Profil unvollständig'}
            </span>
          </div>
        </div>
      </div>

      {editMode ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 sm:p-7">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basisdaten */}
                <div>
                  <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 opacity-80" /> Basisdaten
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <AttributeLabel iconKey="name" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Vorname</AttributeLabel>
                      <input
                        type="text"
                        name="vorname"
                        value={formData.vorname}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <AttributeLabel iconKey="name" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Nachname</AttributeLabel>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <AttributeLabel iconKey="birthday" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Geburtsdatum</AttributeLabel>
                      <input
                        type="text"
                        name="geb_am"
                        value={displayData.geb_am || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <AttributeLabel iconKey="gender" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Geschlecht</AttributeLabel>
                      <input
                        type="text"
                        value={formData.gender === false || formData.gender === 0 ? 'Männlich' : formData.gender === true || formData.gender === 1 ? 'Weiblich' : 'Nicht angegeben'}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* Adresse */}
                <div>
                  <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
                    <HomeIcon className="h-5 w-5 mr-2 opacity-80" /> Adresse
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <AttributeLabel iconKey="street" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Straße *</AttributeLabel>
                      <input
                        type="text"
                        name="strasse"
                        value={formData.strasse}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <AttributeLabel iconKey="address" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">PLZ *</AttributeLabel>
                      <input
                        type="text"
                        name="plz"
                        value={formData.plz}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <AttributeLabel iconKey="city" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Ort *</AttributeLabel>
                      <input
                        type="text"
                        name="ort"
                        value={formData.ort}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Kontakt */}
                <div>
                  <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
                    <EnvelopeIcon className="h-5 w-5 mr-2 opacity-80" /> Kontakt
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <AttributeLabel iconKey="phone" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Telefon</AttributeLabel>
                      <input
                        type="text"
                        name="tel_nummer"
                        value={formData.tel_nummer}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <AttributeLabel iconKey="email" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Email</AttributeLabel>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* Verzehrliste (Self-Service) - nicht für Gäste */}
                {!isGuest && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
                      <CreditCardIcon className="h-5 w-5 mr-2 opacity-80" /> Verzehrliste
                    </h3>
                    <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          Ich möchte auf der Verzehrliste stehen
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Wenn aktiviert, wirst du beim automatischen Export der Verzehrliste aufgenommen. Deaktiviert erscheinst du nicht auf der Liste.
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={formData.verzehrliste === 1}
                        onClick={handleVerzehrlisteToggle}
                        className={`${formData.verzehrliste === 1 ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'} relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                      >
                        <span
                          className={`${formData.verzehrliste === 1 ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* Vereinsdaten */}
                {!isGuest && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 mr-2 opacity-80" /> Vereinsdaten
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <AttributeLabel iconKey="memberSince" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Mitglied seit</AttributeLabel>
                        <input
                          type="text"
                          name="eintritt"
                          value={formData.eintritt}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="role" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Vereinsposition</AttributeLabel>
                        <input
                          type="text"
                          name="vereinsposition"
                          value={formData.vereinsposition}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="payment" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Mitglied bezahlt ({currentYear})</AttributeLabel>
                        <input
                          type="text"
                          name="mitgliedBezahlt"
                          value={formData.mitgliedBezahlt}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Datenschutz und Öffentlichkeit */}
                {!isGuest && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-3 pb-2 border-b-2 border-gray-200 dark:border-gray-700 flex items-center">
                      <TicketIcon className="h-5 w-5 mr-2 opacity-80" /> Datenschutz und Öffentlichkeit
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <AttributeLabel iconKey="privacy" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Datenschutzerklärung</AttributeLabel>
                        <input
                          type="text"
                          value={displayData.datenschutzerklaerung}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <AttributeLabel iconKey="website" className="mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">Homepage</AttributeLabel>
                        <input
                          type="text"
                          value={displayData.homepage}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                          <ChatBubbleLeftRightIcon className="h-4 w-4" />
                          Facebook
                        </label>
                        <input
                          type="text"
                          value={displayData.facebook}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                          <NewspaperIcon className="h-4 w-4" />
                          Presse
                        </label>
                        <input
                          type="text"
                          value={displayData.presse}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white opacity-60 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </form>
        </div>
      ) : (
        <>
          {/* Kennzahlen (v3 Stat-Kacheln) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile
              icon={getAttributeIcon('role') as IconComp}
              label="Funktion"
              value={formData.vereinsposition || 'Mitglied'}
            />
            <StatTile
              icon={getAttributeIcon('memberSince') as IconComp}
              label="Mitglied seit"
              value={memberSinceYear}
              mono
            />
            {!isGuest && (
              <StatTile
                icon={getAttributeIcon('amount') as IconComp}
                label={`Beitrag ${currentYear}`}
                value={formData.beitrag_befreit ? 'Beitragsfrei' : formData.beitrag_bezahlt ? 'Bezahlt' : 'Offen'}
                valueClass={
                  formData.beitrag_befreit || formData.beitrag_bezahlt
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-warning-600 dark:text-warning-400'
                }
                sub={
                  formData.beitrag_befreit ? 'Ehrenmitglied am 1. Januar' : formData.beitrag_bezahlt && formData.beitrag_datum
                    ? `am ${formatGermanDate(formData.beitrag_datum)}`
                    : undefined
                }
              />
            )}
            {!isGuest && (
              <StatTile
                icon={getAttributeIcon('receipt') as IconComp}
                label="Verzehrliste"
                value={displayData.verzehrliste || 'Ja'}
                valueClass={
                  (displayData.verzehrliste || 'Ja') === 'Ja'
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-gray-400'
                }
              />
            )}
          </div>

          {/* Stammdaten (v3 gruppiert, mit Icon-Labels) */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-6 sm:p-7 flex flex-col gap-6">
            {/* Basisdaten */}
            <div>
              <SectionTitle icon={getAttributeIcon('member') as IconComp}>Basisdaten</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                <InfoRow icon={getAttributeIcon('name') as IconComp} label="Vorname">
                  {displayData.vorname || 'Nicht angegeben'}
                </InfoRow>
                <InfoRow icon={getAttributeIcon('name') as IconComp} label="Nachname">
                  {displayData.name || 'Nicht angegeben'}
                </InfoRow>
                <InfoRow icon={getAttributeIcon('birthday') as IconComp} label="Geburtsdatum" mono>
                  {displayData.geb_am || 'Nicht angegeben'}
                </InfoRow>
                <InfoRow icon={getAttributeIcon('gender') as IconComp} label="Geschlecht">
                  {genderLabel}
                </InfoRow>
              </div>
            </div>

            {/* Adresse */}
            <div>
              <SectionTitle icon={getAttributeIcon('houseNumber') as IconComp}>Adresse</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                <InfoRow icon={getAttributeIcon('street') as IconComp} label="Straße">
                  {displayData.strasse || 'Nicht angegeben'}
                </InfoRow>
                <InfoRow icon={getAttributeIcon('address') as IconComp} label="PLZ, Ort">
                  {displayData.plz && displayData.ort ? `${displayData.plz} ${displayData.ort}` : 'Nicht angegeben'}
                </InfoRow>
              </div>
            </div>

            {/* Kontakt */}
            <div>
              <SectionTitle icon={getAttributeIcon('email') as IconComp}>Kontakt</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                <InfoRow icon={getAttributeIcon('phone') as IconComp} label="Telefon" mono>
                  {displayData.tel_nummer || 'Nicht angegeben'}
                </InfoRow>
                <InfoRow icon={getAttributeIcon('email') as IconComp} label="E-Mail">
                  {displayData.email ? (
                    <a
                      href={`mailto:${displayData.email}`}
                      className="text-primary-600 dark:text-primary-400 hover:underline break-all"
                    >
                      {displayData.email}
                    </a>
                  ) : (
                    'Nicht angegeben'
                  )}
                </InfoRow>
              </div>
            </div>
          </div>

          {/* Datenschutz & Öffentlichkeit (v3, mit Icon-Labels + Ja/Nein-Badges) */}
          {!isGuest && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-6 sm:p-7">
              <SectionTitle icon={getAttributeIcon('security') as IconComp}>Datenschutz &amp; Öffentlichkeit</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                <InfoRow icon={getAttributeIcon('privacy') as IconComp} label="Datenschutzerklärung">
                  <YesNoBadge yes={displayData.datenschutzerklaerung === 'Ja'} />
                </InfoRow>
                <InfoRow icon={getAttributeIcon('website') as IconComp} label="Homepage">
                  <YesNoBadge yes={displayData.homepage === 'Ja'} />
                </InfoRow>
                <InfoRow icon={ChatBubbleLeftRightIcon} label="Facebook">
                  <YesNoBadge yes={displayData.facebook === 'Ja'} />
                </InfoRow>
                <InfoRow icon={NewspaperIcon} label="Presse">
                  <YesNoBadge yes={displayData.presse === 'Ja'} />
                </InfoRow>
              </div>
            </div>
          )}

          {/* Browser-Benachrichtigungen (Terminerinnerung am Vortag) */}
          <PushNotificationSettings />
        </>
      )}

      {/* Security Section (v3: versenkte Sicherheits-Leiste) */}
      <section className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 sm:p-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center gap-3">
            <LockClosedIcon className="h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="flex flex-col">
              <span className="text-base font-bold text-gray-900 dark:text-white">Sicherheit</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{passkeySubtitle}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
              className="inline-flex h-9 w-full sm:w-auto items-center justify-center gap-2 rounded-lg px-3.5 font-semibold text-sm text-gray-700 dark:text-gray-200 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
              <KeyIcon className="h-4 w-4" />
              Passwort ändern
            </button>
            {passkeySupported && (
              <button
                type="button"
                onClick={handleAddPasskey}
                disabled={passkeyBusy}
                className="inline-flex h-9 w-full sm:w-auto items-center justify-center gap-2 rounded-lg px-3.5 font-semibold text-sm bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300 transition-colors hover:bg-primary-100 dark:hover:bg-primary-900/40 disabled:opacity-50"
              >
                <FingerPrintIcon className="h-4 w-4" />
                Passkey hinzufügen
              </button>
            )}
          </div>
        </div>

        {/* Passkey-Liste */}
        {passkeySupported && passkeys.length > 0 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-5 sm:p-6">
            <h3 className="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-3">
              Registrierte Passkeys
            </h3>
            <ul className="space-y-2">
              {passkeys.map(pk => (
                <li
                  key={pk.id}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                      <KeyIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {pk.device_name || 'Unbenannter Passkey'}
                        {pk.backed_up ? (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
                            Synchronisiert
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Erstellt: {new Date(pk.created_at).toLocaleDateString('de-DE')}
                        {pk.last_used_at && (
                          <> · Zuletzt: {new Date(pk.last_used_at).toLocaleDateString('de-DE')}</>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeletePasskey(pk.id)}
                    disabled={passkeyBusy}
                    className="p-2 text-gray-400 hover:text-error-600 dark:hover:text-error-400 transition-colors disabled:opacity-50 flex-shrink-0"
                    title="Passkey entfernen"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Password Change Modal */}
      <ModalShell
        open={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        }}
        variant="form"
        size="sm"
        accent="brand"
        icon={KeyIcon}
        title="Passwort ändern"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{
              label: 'Abbrechen',
              onClick: () => {
                setShowPasswordModal(false);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              },
            }}
            primary={{
              label: 'Ändern',
              onClick: handlePasswordChange,
              disabled: !canSubmitPassword,
            }}
          />
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Aktuelles Passwort
            </label>
            <input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Neues Passwort
            </label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {/* Passwort-Regeln */}
            <ul className="mt-2 space-y-1">
              {passwordRules.map((rule) => {
                const ok = rule.test(passwordData.newPassword);
                return (
                  <li key={rule.label} className="flex items-center gap-1.5 text-xs">
                    {ok ? (
                      <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                    )}
                    <span className={ok ? 'text-success-700 dark:text-success-400' : 'text-gray-500 dark:text-gray-400'}>
                      {rule.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Neues Passwort bestätigen
            </label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {passwordData.confirmPassword.length > 0 && !passwordsMatch && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400">
                <XCircleIcon className="h-4 w-4 flex-shrink-0" />
                Die Passwörter stimmen nicht überein
              </p>
            )}
          </div>
        </div>
      </ModalShell>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, visible: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default Profile;
